import { createSlice } from "@reduxjs/toolkit";
import { CALL_BODY_SCREEN } from "../constants/Constants";
import { singleUserEnabled } from "../shared/utility";
import { GLOBAL_MEETING_DATA } from "../constants/PublicVariables";

const initialState = {
    currentCallBodyScreen: CALL_BODY_SCREEN.CONNECTING_SCREEN,
    isParticipantDrawerOpen: false,
    isNotesDrawerOpen: false,
    isBookmarkOpen: false,
    isChatDrawerOpen: false,
    isAskAIDrawerOpen: false,
    isSessionInfoOpen: false,
    isCallAccepted: false,
    isLanguageDropdownBtnVisible: true,
    callDuration: 0,
    timerStarted: false,
    participantCount: 0,
    isRoomConnected: false,
    isRemoteParticipantJoined: false,
    isCallDisconnected: false,
    askUserPopup: false,
    countinueWithWeb: false,
    showLocationPermissionPopup: false,
    expandView: true,
    oppScreenshare: false,
    activeuserlist: {},
    newActiveUserList: {},
    totalUserCount: 0,
    ownIndex: 0,
    callParticipants: [],
    isCallSettingDrawerOpen: false,
    supportLinkForAgent: null,
    currentZoom: 1,
    participantIdentitiesArrayForUploadMap: [],
    allowMarkup: true,
    allowTakePhoto: true,
    allowPointer: true,
    waitingBarWidth: 0,
    strStartsIn: null,
    isConfirmMultiStreamOpen: false,
    isMobileSessionInfoVisible: true,
    isLocationOnVideoIsVisible: false,
    locationOnVideoDetails: {},
    joinedUserUserId: [],
    remoteParticipantesDeviceInfo: {},
    OppoDetails: {
        // remote user details
        deviceType: GLOBAL_MEETING_DATA.device_type,
        oppOrientation: "3",
        lat: 0,
        long: 0,
    },
    isAppDownloadModalOpen: false,
    isFirstStepCompletedOfAppDownload: false,
    confirmDisconnectCall: false,
    showCallDisconnectedPopup: false,
    partiCountForDisplay: 0,
    isHideVideo: false,
    isUnSupportedBrowserModalOpen: false,
    isLowerVersionModalOpen: false,
    isIOSBrowserCheckModalOpen: false,
    isWebViewFoundModalOpen: false,
    isAndroidBrowserCheckModalOpen: false,
    isAndroidWebViewModalOpen: false,
    hasUserAcceptedWebView: false,
    audioPermissionDeniedMsg: false,
    videoPermissionDeniedMsg: false,
    audioVideoBlockforPopup: false,
    isSlickViewOpen: false,
    nqAvailable: true,
    isVideoButtonClickDisable: false,
    isAudioButtonClickDisable: false,
    removeParticipant: {
        isRemoveAllModalOpen: false,
        isRemoveModalOpen: false,
        identity: "",
        name: "",
    },
    isUnableToJoinCallModalOpen: false,
    videoStartNotificationReceivedFromHostModalOpen: false,
    audioStartNotificationReceivedFromHostModalOpen: false,
    screenShareStartNotificationReceivedFromHostModalOpen: false,
    isIosPlayPauseIconModalOpen: false,
    requestMicCameraModalsData: [],
    isStartCameraAsPerAgentRequestPermission: false,
    shouldAutoAcceptPrivacyPolicy: false,
};

const DRAWER_TYPE = {
    PARTICIPANT: "participant",
    NOTES: "notes",
    BOOKMARK: "bookmark",
    CHAT: "chat",
    ASKAI: "askAI",
    CALL_SETTING: "callSetting",
};

const setIsDrawerOpen = (state, action, drawerName) => {
    state.isParticipantDrawerOpen = drawerName === DRAWER_TYPE.PARTICIPANT ? action.payload : false;
    state.isNotesDrawerOpen = drawerName === DRAWER_TYPE.NOTES ? action.payload : false;
    state.isBookmarkOpen = drawerName === DRAWER_TYPE.BOOKMARK ? action.payload : false;
    state.isChatDrawerOpen = drawerName === DRAWER_TYPE.CHAT ? action.payload : false;
    state.isAskAIDrawerOpen = drawerName === DRAWER_TYPE.ASKAI ? action.payload : false;
    state.isCallSettingDrawerOpen = drawerName === DRAWER_TYPE.CALL_SETTING ? action.payload : false;
};

const commonReducerSlice = createSlice({
    name: "commonReducer",
    initialState,
    reducers: {
        setCurrentCallBodyScreen(state, action) {
            state.currentCallBodyScreen = action.payload;
        },
        setIsParticipantDrawerOpen(state, action) {
            setIsDrawerOpen(state, action, DRAWER_TYPE.PARTICIPANT);
        },
        setIsNotesDrawerOpen(state, action) {
            setIsDrawerOpen(state, action, DRAWER_TYPE.NOTES);
        },
        setIsBookmarkOpen(state, action) {
            setIsDrawerOpen(state, action, DRAWER_TYPE.BOOKMARK);
        },
        setIsChatDrawerOpen(state, action) {
            setIsDrawerOpen(state, action, DRAWER_TYPE.CHAT);
        },
        setIsAskAIDrawerOpen(state, action) {
            setIsDrawerOpen(state, action, DRAWER_TYPE.ASKAI);
        },
        setIsSessionInfoOpen(state, action) {
            state.isSessionInfoOpen = action.payload;
        },
        setIsRoomConnected(state, action) {
            state.isRoomConnected = action.payload;
            state.isCallDisconnected = false;
            if (singleUserEnabled()) {
                state.isRemoteParticipantJoined = true;
            } else {
                state.isRemoteParticipantJoined = false;
            }
        },
        setIsCallDisconnected(state, action) {
            state.isRoomConnected = false;
            state.isCallDisconnected = action.payload;
            state.isRemoteParticipantJoined = false;
        },
        setIsRemoteParticipantJoined(state, action) {
            if (action.payload) {
                state.isRemoteParticipantJoined = action.payload;
            }
        },
        setCallDuration(state, action) {
            state.callDuration = action.payload;
        },
        setTimerStarted(state, action) {
            state.timerStarted = action.payload;
        },
        setParticipantCount(state, action) {
            state.participantCount = action.payload;
        },
        setAskUserPopup(state, action) {
            state.askUserPopup = action.payload;
        },
        setCountinueWithWeb(state, action) {
            state.countinueWithWeb = action.payload;
        },
        setShowLocationPermissionPopup(state, action) {
            state.showLocationPermissionPopup = action.payload;
        },
        setExpandView(state, action) {
            state.expandView = action.payload;
        },
        setOppScreenshare(state, action) {
            state.oppScreenshare = action.payload;
        },
        setActiveuserlist(state, action) {
            state.activeuserlist = action.payload;
        },
        setNewActiveUserlist(state, action) {
            state.newActiveUserList = action.payload;
        },
        setTotalUserCount(state, action) {
            state.totalUserCount = action.payload;
        },
        setCallParticipants(state, action) {
            state.callParticipants = action.payload;
        },
        setOwnIndex(state, action) {
            state.ownIndex = action.payload;
        },
        setIsCallSettingDrawerOpen(state, action) {
            setIsDrawerOpen(state, action, DRAWER_TYPE.CALL_SETTING);
        },
        setIsLanguageDropdownBtnVisible(state, action) {
            state.isLanguageDropdownBtnVisible = action.payload;
        },
        setSupportLinkForAgent(state, action) {
            state.supportLinkForAgent = action.payload;
        },
        setCurrentZoom(state, action) {
            state.currentZoom = action.payload;
        },
        setParticipantIdentitiesArrayForUploadMap(state, action) {
            state.participantIdentitiesArrayForUploadMap = action.payload;
        },
        setAllowMarkup(state, action) {
            state.allowMarkup = action.payload;
        },
        setAllowTakePhoto(state, action) {
            state.allowTakePhoto = action.payload;
        },
        setAllowPointer(state, action) {
            state.allowPointer = action.payload;
        },
        setWaitingBarWidth(state, action) {
            state.waitingBarWidth = action.payload;
        },
        setStrStartsIn(state, action) {
            state.strStartsIn = action.payload;
        },
        setIsConfirmMultiStreamOpen(state, action) {
            state.isConfirmMultiStreamOpen = action.payload;
        },
        setIsMobileSessionInfoVisible(state, action) {
            state.isMobileSessionInfoVisible = action.payload;
        },
        setIsLocationOnVideoIsVisible(state, action) {
            state.isLocationOnVideoIsVisible = action.payload;
        },
        setLocationOnVideoDetails(state, action) {
            state.locationOnVideoDetails = action.payload;
        },
        setJoinedUserUserId(state, action) {
            state.joinedUserUserId = action.payload;
        },
        setRemoteParticipantesDeviceInfo(state, action) {
            state.remoteParticipantesDeviceInfo = action.payload;
        },
        setOppoDetails(state, action) {
            state.OppoDetails = action.payload;
        },
        setIsAppDownloadModalOpen(state, action) {
            state.isAppDownloadModalOpen = action.payload;
        },
        setIsFirstStepCompletedOfAppDownload(state, action) {
            state.isFirstStepCompletedOfAppDownload = action.payload;
        },
        setConfirmDisconnectCall(state, action) {
            state.confirmDisconnectCall = action.payload;
        },
        setShowCallDisconnectedPopup(state, action) {
            state.showCallDisconnectedPopup = action.payload;
        },
        setPartiCountForDisplay(state, action) {
            state.partiCountForDisplay = action.payload;
        },
        setIsHideVideo(state, action) {
            state.isHideVideo = action.payload;
        },
        setIsUnSupportedBrowserModalOpen(state, action) {
            state.isUnSupportedBrowserModalOpen = action.payload;
        },
        setIsLowerVersionModalOpen(state, action) {
            state.isLowerVersionModalOpen = action.payload;
        },
        setIsIOSBrowserCheckModalOpen(state, action) {
            state.isIOSBrowserCheckModalOpen = action.payload;
        },
        setIsWebViewFoundModalOpen(state, action) {
            state.isWebViewFoundModalOpen = action.payload;
        },
        setIsAndroidBrowserCheckModalOpen(state, action) {
            state.isAndroidBrowserCheckModalOpen = action.payload;
        },
        setIsAndroidWebViewModalOpen(state, action) {
            state.isAndroidWebViewModalOpen = action.payload;
        },
        setHasUserAcceptedWebView(state, action) {
            state.hasUserAcceptedWebView = action.payload;
        },
        setAudioPermissionDeniedMsg(state, action) {
            state.audioPermissionDeniedMsg = action.payload;
        },
        setVideoPermissionDeniedMsg(state, action) {
            state.videoPermissionDeniedMsg = action.payload;
        },
        setAudioVideoBlockforPopup(state, action) {
            state.audioVideoBlockforPopup = action.payload;
        },
        setIsSlickViewOpen(state, action) {
            state.isSlickViewOpen = action.payload;
        },
        setNqAvailable(state, action) {
            state.nqAvailable = action.payload;
        },
        setIsVideoButtonClickDisable(state, action) {
            state.isVideoButtonClickDisable = action.payload;
        },
        setIsAudioButtonClickDisable(state, action) {
            state.isAudioButtonClickDisable = action.payload;
        },
        setRemoveParticipant(state, action) {
            state.removeParticipant = action.payload;
        },
        setIsUnableToJoinCallModalOpen(state, action) {
            state.isUnableToJoinCallModalOpen = action.payload;
        },
        setVideoStartNotificationReceivedFromHostModalOpen(state, action) {
            state.videoStartNotificationReceivedFromHostModalOpen = action.payload;
        },
        setAudioStartNotificationReceivedFromHostModalOpen(state, action) {
            state.audioStartNotificationReceivedFromHostModalOpen = action.payload;
        },
        setScreenShareStartNotificationReceivedFromHostModalOpen(state, action) {
            state.screenShareStartNotificationReceivedFromHostModalOpen = action.payload;
        },
        setIsIosPlayPauseIconModalOpen(state, action) {
            state.isIosPlayPauseIconModalOpen = action.payload;
        },
        setRequestMicCameraModalsData(state, action) {
            state.requestMicCameraModalsData = action.payload;
        },
        setIsStartCameraAsPerAgentRequestPermission(state, action) {
            state.isStartCameraAsPerAgentRequestPermission = action.payload;
        },
        setShouldAutoAcceptPrivacyPolicy(state, action) {
            state.shouldAutoAcceptPrivacyPolicy = action.payload;
        },

        resetCommonReducer(_state, action) {
            return {
                ...initialState,
                showCallDisconnectedPopup: action.payload,
            };
        },
    },
});

export const {
    setCurrentCallBodyScreen,
    setIsParticipantDrawerOpen,
    setIsNotesDrawerOpen,
    setIsBookmarkOpen,
    setIsChatDrawerOpen,
    setIsAskAIDrawerOpen,
    setIsSessionInfoOpen,
    setIsRoomConnected,
    setIsCallDisconnected,
    setIsRemoteParticipantJoined,
    setCallDuration,
    setTimerStarted,
    setParticipantCount,
    setAskUserPopup,
    setCountinueWithWeb,
    setShowLocationPermissionPopup,
    setExpandView,
    setOppScreenshare,
    setActiveuserlist,
    setNewActiveUserlist,
    setTotalUserCount,
    setCallParticipants,
    setOwnIndex,
    setIsCallSettingDrawerOpen,
    setIsLanguageDropdownBtnVisible,
    setSupportLinkForAgent,
    setCurrentZoom,
    setParticipantIdentitiesArrayForUploadMap,
    setAllowMarkup,
    setAllowTakePhoto,
    setAllowPointer,
    setWaitingBarWidth,
    setStrStartsIn,
    setIsConfirmMultiStreamOpen,
    setIsMobileSessionInfoVisible,
    setIsLocationOnVideoIsVisible,
    setLocationOnVideoDetails,
    setJoinedUserUserId,
    setRemoteParticipantesDeviceInfo,
    setIsAppDownloadModalOpen,
    setIsFirstStepCompletedOfAppDownload,
    setConfirmDisconnectCall,
    resetCommonReducer,
    setShowCallDisconnectedPopup,
    setOppoDetails,
    setPartiCountForDisplay,
    setIsHideVideo,
    setIsUnSupportedBrowserModalOpen,
    setIsLowerVersionModalOpen,
    setIsIOSBrowserCheckModalOpen,
    setIsWebViewFoundModalOpen,
    setIsAndroidBrowserCheckModalOpen,
    setIsAndroidWebViewModalOpen,
    setHasUserAcceptedWebView,
    setAudioPermissionDeniedMsg,
    setVideoPermissionDeniedMsg,
    setAudioVideoBlockforPopup,
    setIsSlickViewOpen,
    setNqAvailable,
    setIsVideoButtonClickDisable,
    setIsAudioButtonClickDisable,
    setRemoveParticipant,
    setIsUnableToJoinCallModalOpen,
    setAudioStartNotificationReceivedFromHostModalOpen,
    setVideoStartNotificationReceivedFromHostModalOpen,
    setScreenShareStartNotificationReceivedFromHostModalOpen,
    setIsIosPlayPauseIconModalOpen,
    setRequestMicCameraModalsData,
    setIsStartCameraAsPerAgentRequestPermission,
    setShouldAutoAcceptPrivacyPolicy,
} = commonReducerSlice.actions;
export default commonReducerSlice.reducer;
